/**
 * NewHome.jsx
 *
 * This component displays the Performance Metrics dashboard for the landing page.
 * - Shows metric cards in rows and columns (4 per row) inside a styled box.
 * - Allows users to customize which metrics are shown via a modal dialog.
 * - Modal supports drag-and-drop reordering and checkbox selection of metrics.
 * - Only checked metrics are displayed after saving.
 * - Includes controls for expanding/collapsing the metrics view and opening the modal.
 * - Hide/Show Metrics toggle collapses the section  (persisted per user per tenant); no metrics are fetched while hidden.
 */

import React, { useRef, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SmartCardOpportunities from './SmartCardOpportunities';
import ReportSummary from './ReportSummary';
import { useSelector, useDispatch } from 'react-redux';
import PerformanceMetrics from './PerformanceMetrics';
import { capitalizeWords, getMetricsHiddenPref, setMetricsHiddenPref } from './newHomeHelper';
import { hasPermission } from 'utils/common';
import { AppConfig } from 'AppConfig';
import Inspection from 'features/frontoffice/component/inspection';
import { Grid } from '@mui/system';
import { Collapse, IconButton } from '@mui/material';
import { IconChevronDown, IconChevronUp, IconPencil } from '@tabler/icons-react';
import * as Types from 'features/frontoffice/actions/types';
import * as Constant from 'common/Constant';
import { useSmartCardVdsTrigger } from 'features/frontoffice/useSmartCardVdsTrigger';


const MetricsHeaderButton = ({ onClick, fullWidth, icon: Icon, label, ...rest }) => (
  <IconButton
    sx={{
      border: '1px solid var(--ctev-blue, #2F54A5)',
      borderRadius: '24px',
      py: '8px',
      px: '12px',
      backgroundColor: '#ffffff',
      color: '#2F54A5',
      fontWeight: '600',
      fontSize: '14px',
      lineHeight: '150%',
      fontFamily: 'Noto Sans',
      '&:hover': {
        backgroundColor: '#D8E6FF',
        borderColor: '#001A47',
        color: '#001A47',
      },
      ...(fullWidth && { width: '100%', justifyContent: 'center' }),
    }}
    style={{ outline: 'none' }}
    onClick={onClick}
    size="small"
    {...rest}
  >
    <Icon height={16} width={16} style={{ marginRight: 6 }} /> {label}
  </IconButton>
);

const ModifyMetricsButton = ({ onClick, fullWidth }) => (
  <MetricsHeaderButton onClick={onClick} fullWidth={fullWidth} icon={IconPencil} label="Modify Metrics" />
);

// Both labels share one grid cell so the button is always as wide as the longer one and
// doesn't shift when toggled; the inactive label is invisible and hidden from screen readers.
const ToggleMetricsLabel = ({ hidden }) => (
  <Box component="span" sx={{ display: 'inline-grid', '& > span': { gridArea: '1 / 1', textAlign: 'center' } }}>
    <span aria-hidden={hidden} style={{ visibility: hidden ? 'hidden' : 'visible' }}>Hide Metrics</span>
    <span aria-hidden={!hidden} style={{ visibility: hidden ? 'visible' : 'hidden' }}>Show Metrics</span>
  </Box>
);

const ToggleMetricsButton = ({ hidden, onClick, fullWidth }) => (
  <MetricsHeaderButton
    onClick={onClick}
    fullWidth={fullWidth}
    icon={hidden ? IconChevronDown : IconChevronUp}
    label={<ToggleMetricsLabel hidden={hidden} />}
    aria-expanded={!hidden}
    aria-controls="performance-metrics-section"
  />
);

const NewHome = () => {
  const user = useSelector((state) => state.user);
  const showMetrics = user.sysAdmin || user.foAdmin || hasPermission(AppConfig.PERMISSION.FO_METRICS, user.permissions);
  const openReportViewDialog = useSelector((state) => state.fo.openReportViewDialog);
  const reportOriginComponent = useSelector((state) => state.fo.reportOriginComponent);
  const dispatch = useDispatch();
  const smartCardOpportunitiesRef = useRef();
  const didOriginScrollRef = useRef(false);

  const performanceMetricsRef = useRef();
  const [hasVisibleMetrics, setHasVisibleMetrics] = React.useState(null);
  const [hasMetricsData, setHasMetricsData] = React.useState(true);
  const [tenantHasData, setTenantHasData] = React.useState(true);

  // Hide/Show state for the Performance Metrics section, persisted per user per tenant.
  // Derived synchronously so it updates in the same render the new tenant is committed.
  const [metricsHiddenVersion, setMetricsHiddenVersion] = useState(0);
  const metricsHidden = useMemo(
    () => getMetricsHiddenPref(user.email, user.currentTenantCd),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user.email, user.currentTenantCd, metricsHiddenVersion]
  );

  // currentTenantCd stays on the outgoing tenant until the switch commits, so keep the
  // tenant-scoped cards unmounted while a switch is pending. Display-only; never persisted.
  const metricsSectionHidden = metricsHidden || user.isUpdating;

  const handleToggleMetrics = () => {
    setMetricsHiddenPref(user.email, user.currentTenantCd, !metricsHidden);
    setMetricsHiddenVersion((v) => v + 1);
  };

  // Refresh Smart Card values from /tbl/sc-vds when global filters change.
  useSmartCardVdsTrigger();

   const handleOpenModal = () => {
    if (performanceMetricsRef.current) {
      performanceMetricsRef.current.handleOpenModal();
    }
  }

  const handleMetricsChange = React.useCallback((visible, hasData, tenantData) => {
    setHasVisibleMetrics(visible);
    setHasMetricsData(hasData);
    setTenantHasData(tenantData);
  }, []);

  useEffect(() => {
    if (!showMetrics) {
      setHasVisibleMetrics(null);
      setHasMetricsData(true);
      setTenantHasData(true);
    }
  }, [showMetrics]);

  const performanceMetricSubText = hasVisibleMetrics
    ? 'Here\'s your plan\'s current performance and metrics, based on the latest data.'
    : hasMetricsData
      ? 'Modify Metrics to select one or more metrics to display here.'
      : tenantHasData ? 'No performance metrics are available for the selected filters.' : 'Performance Metrics will appear once sufficient data becomes available.';

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const previousMode = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = previousMode;
      };
    }
  }, []);

  useEffect(() => {
    if (!openReportViewDialog) {
      let reportOrigin = null;
      try {
        const raw = sessionStorage.getItem('reportOrigin');
        reportOrigin = raw ? JSON.parse(raw) : null;
      } catch (e) {
        reportOrigin = null;
      }
      if (reportOriginComponent === Constant.REPORT_SUMMARY_HOME || reportOrigin?.reportOriginComponent === Constant.REPORT_SUMMARY_HOME) {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        dispatch({ type: Types.RESET_REPORT_ORIGIN_COMPONENT });
        sessionStorage.removeItem('reportOrigin');
        didOriginScrollRef.current = true;
      } else if (reportOriginComponent === Constant.TOP_SMART_CARDS || reportOrigin?.reportOriginComponent === Constant.TOP_SMART_CARDS) {
        const el = smartCardOpportunitiesRef.current;
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
        dispatch({ type: Types.RESET_REPORT_ORIGIN_COMPONENT });
        sessionStorage.removeItem('reportOrigin');
        didOriginScrollRef.current = true;
      } else if (!didOriginScrollRef.current) {
        window.scrollTo({ top: 0 });
      } else {
        didOriginScrollRef.current = false;
      }
    }
  }, [openReportViewDialog, reportOriginComponent, dispatch]);

  return (
    <Box
      sx={{
        bgcolor: '#FFF',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mx: '-60px',    // counteract root level 60px left/right padding
      }}>
      <Box
        sx={{
          width: '100%',
          px: 0,
        }}>
        {/* Greeting above Performance Metrics Box */}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'start',
            bgcolor: '#EFF5FF', 
            px: { xs: 2, sm: 6, md: 6 },
            py: 4,
            width: '100%',
          }}>
          <Inspection isNewHome showRefreshStatus />
          <Grid container justifyContent="space-between" width='100%'>
            <Grid item>
              <Typography
                sx={{
                  fontFamily: 'Rubik',
                  fontSize: {
                    xs: '20px',
                    sm: '20px',
                    lg: '20px',
                    xl: '20px'
                  },
                  fontStyle: 'normal',
                  fontWeight: 600,
                  color: '#000',
                  lineHeight: {
                    xs: '24px',
                    md: '30px',
                    lg: '42px'
                  }
                }}>
                {user?.firstName ? `Welcome, ${capitalizeWords(user.firstName)}` : 'Welcome'}
              </Typography>
            </Grid>
            {showMetrics && (
            <Grid item sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'flex-start', gap: '12px' }}>
              <ModifyMetricsButton onClick={handleOpenModal} />
              <ToggleMetricsButton hidden={metricsHidden} onClick={handleToggleMetrics} />
            </Grid>
            )}
          </Grid>
           {showMetrics && hasVisibleMetrics !== null && (
           <Collapse in={!metricsSectionHidden} timeout={300} sx={{ width: '100%' }}>
           <Typography sx={{
             mt: '8px',
             fontFamily: 'Noto Sans',
             fontSize: '14px',
             fontStyle: 'normal',
             fontWeight: 400,
             color: '#000',
            lineHeight: '21px'
           }}>
             {performanceMetricSubText}
           </Typography>
           </Collapse>
           )}
            {/* Mobile-only Modify Metrics button */}
            {showMetrics && (
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: '12px', width: '100%', mt: 3 }}>
              <ModifyMetricsButton onClick={handleOpenModal} fullWidth />
              <ToggleMetricsButton hidden={metricsHidden} onClick={handleToggleMetrics} fullWidth />
            </Box>
            )}
           {/* 1. PerformanceMetrics - Fast, appears first (0ms delay) */}
           {showMetrics && (
           <Box
             id="performance-metrics-section"
             sx={{
               animation: 'fadeInUp 0.4s ease-out',
               animationFillMode: 'both',
               animationDelay: '0s',
               width:1
             }}>
             <PerformanceMetrics ref={performanceMetricsRef} onMetricsChange={handleMetricsChange} hidden={metricsSectionHidden}/>
           </Box>
           )}
        </Box>
        <Box sx={{ mt: 4, marginBottom: '20px', px: { xs: 2, sm: 6, md: 6 }, }}>
          {/* 2. SmartCardOpportunities - Slow (3s), has its own skeleton and animation */}
          <SmartCardOpportunities ref={smartCardOpportunitiesRef} />

          {/* 3. ReportSummary - Fast, appears second (200ms delay) */}
          <Box
            sx={{
              animation: 'fadeInUp 0.4s ease-out',
              animationFillMode: 'both',
              animationDelay: '0.2s'
            }}>
            <ReportSummary />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NewHome;
